import produce from 'immer';
import React from 'react';
import {
    AuthState,
    UserIModule,
    UserIReplaceUserPermissions,
    UserIUserPermissions
} from '../../AppShell/Authentication/AuthorisationModels';
import { ButtonMode } from '../../Components/Button';
import { StyledDialogActions } from '../../Components/Dialog/StyledDialogActions';
import { StyledDialogButton } from '../../Components/Dialog/StyledDialogButton';
import { StyledDialogContent } from '../../Components/Dialog/StyledDialogContent';
import { LoadingPlaceholder } from '../../Components/Placeholder';
import { StyledNavigationPrompt } from '../../Components/Prompts/StyledNavigationPrompt';
import { PermissionSet } from '../../PermissionSet';
import styles from './UserFilePermissions.module.scss';

type UserPermissionsProps = {
    isDeleting: boolean;
    permissions?: UserIUserPermissions;
    onClose(): void;
    loadPermissions(): void;
    onError(error: string): void;
    onUpdatePermissions(permissions: UserIReplaceUserPermissions): Promise<boolean>;
};

type UserPermissionsState = {
    initialModules: UserIModule[];
    modules: UserIModule[];
};

export class UserPermissions extends React.Component<UserPermissionsProps, UserPermissionsState> {
    constructor(props: UserPermissionsProps) {
        super(props);
        this.state = {
            initialModules: [],
            modules: []
        };
    }

    public componentWillUnmount() {
        this.props.onError('');
    }

    public componentDidMount() {
        if (this.props.permissions)
            this.setState({
                initialModules: this.props.permissions.modules,
                modules: this.props.permissions.modules
            });
    }

    public formDirty = () => {
        if (!this.state.initialModules || !this.state.modules) return false;

        return JSON.stringify(this.state.initialModules) !== JSON.stringify(this.state.modules);
    };

    public setAll = (moduleIndex: number, state: AuthState) => {
        const newState = produce(this.state.modules, (modules: UserIModule[]) => {
            const permissions = modules[moduleIndex].permissions.map((permission, index) => ({ permission, index }));
            permissions.forEach(permission => {
                modules[moduleIndex].permissions[permission.index].state = state;
                modules[moduleIndex].permissions[permission.index].inherited = false;
            });
        });

        this.setState({ modules: newState });
    };

    public inheritAll = (moduleIndex: number) => {
        const newState = produce(this.state.modules, (modules: UserIModule[]) => {
            const permissions = modules[moduleIndex].permissions.map((permission, index) => ({ permission, index }));
            permissions.forEach(permission => {
                modules[moduleIndex].permissions[permission.index].state = permission.permission.inherritablePermission
                    ? permission.permission.inherritablePermission.state
                    : AuthState.Denied;
                modules[moduleIndex].permissions[permission.index].inherited = true;
            });
        });

        this.setState({ modules: newState });
    };

    public permissionChange = (moduleIndex: number, permissionIndex: number, state: AuthState, inherited: boolean) => {
        this.setState({
            modules: produce(this.state.modules, (draft: UserIModule[]) => {
                draft[moduleIndex].permissions[permissionIndex].state = state;
                draft[moduleIndex].permissions[permissionIndex].inherited = inherited;
            })
        });
    };

    public save = async () => {
        if (!this.props.permissions) return;

        const newPermissions: UserIReplaceUserPermissions = {
            modules: this.state.modules
                .map(module => ({
                    moduleCode: module.moduleCode,
                    permissions: module.permissions
                        .filter(permission => !permission.inherited)
                        .map(permissions => ({
                            definitionCode: permissions.definitionCode,
                            authoringSystem: permissions.authoringSystem,
                            authoringSystemForeignKey: permissions.authoringSystemForeignKey,
                            state: permissions.state,
                            inherited: permissions.inherited
                        }))
                }))
                .filter(module => module.permissions.length > 0)
        };

        try {
            const result = await this.props.onUpdatePermissions(newPermissions);
            if (result) {
                this.setState({
                    initialModules: this.state.modules
                });
                console.log('Success updating permissions');
            } else {
                console.log('Failed to save permissions');
            }
        } catch (error) {
            console.error('Error updating permissions - error: ', error);
        }
    };

    public render() {
        if (!this.props.permissions) return <LoadingPlaceholder />;

        const actions = (
            <>
                <StyledDialogButton mode={ButtonMode.border} testId='cancel' onClick={this.props.onClose}>
                    Cancel
                </StyledDialogButton>
                <StyledDialogButton testId='save' onClick={this.save}>
                    Save
                </StyledDialogButton>
            </>
        );

        return (
            <>
                <StyledDialogContent noPadding>
                    <div className={styles.container}>
                        {this.state.modules.map((module, moduleIndex) => (
                            <PermissionSet
                                key={module.moduleCode + '-' + moduleIndex}
                                module={module}
                                moduleIndex={moduleIndex}
                                onAllowAll={() => this.setAll(moduleIndex, AuthState.Allowed)}
                                onDenyAll={() => this.setAll(moduleIndex, AuthState.Denied)}
                                onInheritAll={() => this.inheritAll(moduleIndex)}
                                permissionChange={this.permissionChange}
                            />
                        ))}
                    </div>
                    <StyledNavigationPrompt when={!this.props.isDeleting && this.formDirty()} onSave={this.save} />
                </StyledDialogContent>
                <StyledDialogActions>{actions}</StyledDialogActions>
            </>
        );
    }
}
